import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { Plane, Ship, Car, Backpack, PawPrint, Fish } from "lucide-react";
import { TravelMapClient } from "@/components/TravelMapClient";


export const Route = createFileRoute("/travel-tips")({
  head: () =>
    seoHead({
      path: "/travel-tips",
      title: "Travel Tips — Getting to Coffman Cove, Alaska",
      description:
        "How to reach Coffman Cove by air, ferry and water taxi, peak fish availability by month, Prince of Wales Island wildlife, and a packing list for your self-guided Alaska trip.",
    }),
  component: TravelTips,
});

type Carrier = { name: string; url: string };

const ROUTES: { city: string; icon: typeof Plane; note: string; carriers: Carrier[] }[] = [
  {
    city: "From Wrangell",
    icon: Plane,
    note: "Direct air and water taxi service into Coffman Cove.",
    carriers: [
      { name: "Sunrise Aviation", url: "https://www.sunriseflights.com" },
      { name: "Breakaway Adventures", url: "https://www.breakawayadventures.com" },
    ],
  },
  {
    city: "From Ketchikan",
    icon: Plane,
    note: "Charter or scheduled flights direct to Coffman Cove.",
    carriers: [
      { name: "Family Air", url: "https://www.familyairtours.com" },
      { name: "Misty Fjords", url: "https://www.mistyfjordsair.com" },
      { name: "SeaWind Aviation", url: "https://www.seawindaviation.com" },
      { name: "Taquan Air", url: "https://www.taquanair.com" },
      { name: "Black Rock Aviation", url: "https://www.blackrockaviation.com" },
    ],
  },
  {
    city: "Via Klawock",
    icon: Ship,
    note: "Air to Klawock, then about a 1 hour drive to Coffman Cove.*",
    carriers: [{ name: "Island Air Express", url: "https://www.flyislandair.com" }],
  },
  {
    city: "Via Hollis",
    icon: Car,
    note: "Ferry to Hollis, then about a 2 hour drive to Coffman Cove.*",
    carriers: [{ name: "Inter-Island Ferry Authority", url: "https://www.interislandferry.com" }],
  },
];

const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct"];

// 2 = most abundant, 1 = present, 0 = least abundant
const SALT: [string, number[]][] = [
  ["King Salmon", [0, 2, 2, 2, 1, 1, 0]],
  ["Coho Salmon", [0, 0, 1, 2, 2, 2, 1]],
  ["Sockeye Salmon", [0, 1, 2, 2, 1, 0, 0]],
  ["Chum Salmon", [0, 1, 2, 2, 2, 1, 0]],
  ["Pink Salmon", [0, 0, 1, 2, 2, 1, 0]],
  ["Dolly Varden", [1, 2, 2, 2, 2, 2, 1]],
  ["Cutthroat Trout", [1, 2, 2, 1, 1, 1, 1]],
  ["Smelt", [1, 2, 1, 0, 0, 0, 0]],
  ["Halibut", [1, 2, 2, 2, 2, 2, 1]],
  ["Rockfish", [1, 2, 2, 2, 2, 2, 1]],
  ["Lingcod", [0, 1, 2, 2, 2, 1, 1]],
];

const FRESH: [string, number[]][] = [
  ["King Salmon", [0, 0, 1, 2, 1, 0, 0]],
  ["Coho Salmon", [0, 0, 0, 1, 2, 2, 1]],
  ["Sockeye Salmon", [0, 0, 1, 2, 1, 0, 0]],
  ["Chum Salmon", [0, 0, 1, 2, 2, 1, 0]],
  ["Pink Salmon", [0, 0, 1, 2, 2, 1, 0]],
  ["Dolly Varden", [1, 1, 2, 2, 2, 2, 1]],
  ["Steelhead Trout", [2, 2, 1, 0, 0, 0, 1]],
  ["Rainbow Trout", [1, 2, 2, 2, 1, 1, 1]],
  ["Cutthroat Trout", [1, 2, 2, 2, 1, 1, 1]],
  ["Brook Trout", [1, 1, 2, 2, 1, 1, 1]],
  ["Grayling", [0, 1, 1, 1, 1, 1, 0]],
  ["Smelt", [1, 1, 0, 0, 0, 0, 0]],
  ["Kokanee", [0, 1, 1, 1, 1, 1, 0]],
];

const PACKING = [
  "Hats and gloves",
  "Fleece / layered clothing",
  "Rain gear and waterproof boots",
  "Waders for river fishing (no felt soles — not permitted in Alaska)",
  "Backpack",
  "Camp shoes or slippers",
  "Toiletries",
  "Polarized sunglasses",
  "Camera",
  "Sunscreen",
  "Mosquito repellent",
  "Small dry bag for the boat",
];

function AbundanceCell({ level }: { level: number }) {
  const cls =
    level === 2
      ? "bg-[var(--color-ember)]"
      : level === 1
        ? "bg-foreground/20"
        : "bg-transparent";
  return (
    <td className="border border-border p-0">
      <div className={`h-6 w-full ${cls}`} />
    </td>
  );
}

function AvailabilityTable({
  title,
  rows,
}: {
  title: string;
  rows: [string, number[]][];
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
      <h3 className="font-display text-xl mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[26rem] border-collapse text-sm">
          <caption className="sr-only">{title} peak availability by month</caption>
          <thead>
            <tr>
              <th scope="col" className="border border-border px-3 py-2 text-left font-semibold">
                Species
              </th>
              {MONTHS.map((m) => (
                <th
                  key={m}
                  scope="col"
                  className="border border-border px-2 py-2 text-center font-semibold"
                >
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([species, levels]) => (
              <tr key={species}>
                <th
                  scope="row"
                  className="border border-border px-3 py-1.5 text-left font-medium whitespace-nowrap"
                >
                  {species}
                </th>
                {levels.map((l, i) => (
                  <AbundanceCell key={MONTHS[i]} level={l} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-6 rounded-sm bg-[var(--color-ember)]" /> Most abundant
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-6 rounded-sm bg-foreground/20" /> Present
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-6 rounded-sm border border-border" /> Least abundant
        </span>
      </div>
    </div>
  );
}

function TravelTips() {
  return (
    <div>
      <section className="bg-[var(--color-deep)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-16">
          <p className="uppercase tracking-[0.3em] text-xs text-white/70">Plan Your Trip</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3 max-w-3xl text-balance text-white">
            Travel tips for Coffman Cove.
          </h1>
          <p className="mt-5 max-w-2xl text-white/90">
            How to get here, when the fish run, what wildlife you'll see, and what to pack for
            your strictly self-guided Alaska trip.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16 space-y-16">
        <section aria-labelledby="getting-here">
          <h2 id="getting-here" className="font-display text-3xl md:text-4xl">
            How to get to Coffman Cove
          </h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Most travelers fly, though ferries and water taxis are popular too. Here's an
            up-to-date list of directions and carriers to save you time.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {ROUTES.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.city}
                  className="rounded-xl border border-border bg-card p-6 transition hover:border-[var(--color-ember)]"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-[var(--color-ember)]" />
                    <h3 className="font-display text-xl">{r.city}</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{r.note}</p>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {r.carriers.map((c) => (
                      <li key={c.name}>
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex rounded-full border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
                        >
                          {c.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-8 overflow-hidden rounded-xl border border-border">
            <TravelMapClient />
          </div>
          <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
            <p>
              <span className="font-semibold text-foreground">By air (solid red):</span> direct
              flights from Wrangell or Ketchikan land at Coffman Cove, or fly into Klawock.
            </p>
            <p>
              <span className="font-semibold text-foreground">By water taxi (blue dots):</span>{" "}
              Wrangell to Coffman Cove across Clarence Strait.
            </p>
            <p>
              <span className="font-semibold text-foreground">By ferry (dashed red):</span>{" "}
              Ketchikan to Hollis with the Inter-Island Ferry Authority.
            </p>
            <p>
              <span className="font-semibold text-foreground">By road (black):</span> Klawock is
              about a 1 hour drive and Hollis about 2 hours to Coffman Cove.*
            </p>
          </div>


          <p className="mt-4 text-xs text-muted-foreground">
            * Island transportation is offered by Coffman Cove Lodging ($175–$200 per vehicle,
            one-way). Ask when you book.
          </p>

        </section>

        <section aria-labelledby="fishing-seasons">
          <div className="flex items-center gap-3">
            <Fish className="h-6 w-6 text-[var(--color-ember)]" />
            <h2 id="fishing-seasons" className="font-display text-3xl md:text-4xl">
              Fishing on Prince of Wales Island
            </h2>
          </div>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            The coasts and rivers around Coffman Cove hold salmon, halibut, red snapper, trout
            and much more. Spring and summer are the most productive months for a self-guided
            trip — you run your own boat and choose your own water.
          </p>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <AvailabilityTable title="Salt water" rows={SALT} />
            <AvailabilityTable title="Fresh water" rows={FRESH} />
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            These charts help you choose the best time to visit. They are not a guarantee that
            you will catch a specific species, and Alaskan waters are not limited to the species
            listed.
          </p>
        </section>

        <section aria-labelledby="wildlife" className="grid gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="flex items-center gap-3">
              <PawPrint className="h-6 w-6 text-[var(--color-ember)]" />
              <h2 id="wildlife" className="font-display text-3xl md:text-4xl">
                Wildlife on POW Island
              </h2>
            </div>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Prince of Wales Island is home to abundant wildlife on land and in the sea. Common
              sightings include black bears (no brown bears on the island), Sitka black-tailed
              deer and gray wolves. Bald eagles and ospreys dive for salmon overhead, and the
              surrounding water hosts some of the best ocean animal sighting in Alaska —
              humpbacks, orcas, belugas, porpoises, dolphins, otters and more.
            </p>
          </div>
          <ul className="grid grid-cols-2 gap-3">
            {[
              "Black bears",
              "Sitka black-tailed deer",
              "Gray wolves",
              "Bald eagles & ospreys",
              "Humpbacks & orcas",
              "Sea otters & porpoises",
            ].map((w) => (
              <li
                key={w}
                className="rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium"
              >
                {w}
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="packing">
          <div className="flex items-center gap-3">
            <Backpack className="h-6 w-6 text-[var(--color-ember)]" />
            <h2 id="packing" className="font-display text-3xl md:text-4xl">
              What to bring on your trip
            </h2>
          </div>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            First trip to Coffman Cove? Here's the short list that covers Southeast Alaska
            weather year-round. Rods, reels, tackle and bait come with boat rentals.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PACKING.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm"
              >
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-ember)]" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-border bg-card p-8 text-center">
          <h2 className="font-display text-2xl md:text-3xl">Ready to lock in your dates?</h2>
          <p className="mt-3 text-muted-foreground">
            Peak season (June–August) books out fast. All trips are strictly self-guided.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/booking"
              className="inline-flex items-center rounded-md bg-[var(--color-deep)] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-[var(--color-ember)]"
            >
              Request dates →
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center rounded-md border border-border px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
            >
              Read the FAQ
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
